/**
 * Emergency Data Fix
 * Repariert korrupte localStorage-Daten sofort
 */

import { listKvKeys, kvGetRaw, kvSetRaw, kvRemove } from '../infrastructure/storage/kvAccess';

/**
 * Repariert alle korrupten Business-Daten sofort
 */
export const emergencyFixCorruptedData = () => {
  try {
    // Finde alle Business-Daten-Schlüssel
    const businessKeys = listKvKeys().filter(
      (key) => key && key.includes('business_') && key.includes('_data')
    );
    
    let fixedCount = 0;
    
    for (const key of businessKeys) {
      try {
        const rawData = kvGetRaw(key);
        if (!rawData) continue;
        
        // Prüfe ob Daten korrupt sind
        if (rawData === '"[object Object]"' || rawData === '[object Object]' || rawData === 'null') {
          // Erstelle leere Business-Daten-Struktur
          const emptyBusinessData = {
            products: [],
            suppliers: [],
            purchases: [],
            sales: [],
            lots: [],
            stockData: {},
            stockLocations: ['Principal'],
            invoices: [],
            invoicePayments: [],
            customers: [],
            orders: [],
            expiredProducts: [],
            supermarketPayments: [],
            supermarketInvoices: [],
            supplierDebts: [],
            workerSessions: [],
            discrepancyManagement: {
              selfDeviations: [],
              handoverDeviations: [],
              workerTolerances: {},
              pendingReviews: [],
              auditTrail: [],
              notifications: [],
              toleranceSystemEnabled: false
            },
            iotData: {
              devices: [],
              alerts: [],
              systemHealth: null,
              monitoringStatus: false,
              lastUpdate: null,
              alertHistory: [],
              deviceMetrics: {},
              temperatureLogs: [],
              humidityLogs: [],
              complianceReports: []
            }
          };
          
          // Speichere mit originalem localStorage (nicht Enhanced Storage)
          kvSetRaw(key, JSON.stringify(emptyBusinessData));
          fixedCount++;
        }
        
      } catch (error) {
        // Error fixing key handled silently
      }
    }
    
    return { success: true, fixedCount };
    
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Bereinigt alle korrupten Daten aus localStorage
 */
export const emergencyCleanupCorruptedData = () => {
  try {
    const keysToRemove = [];

    listKvKeys().forEach((key) => {
      if (!key) return;

      try {
        const value = kvGetRaw(key);
        if (value === '"[object Object]"' || value === '[object Object]' || value === 'null') {
          keysToRemove.push(key);
        }
      } catch (error) {
        keysToRemove.push(key);
      }
    });
    
    // Entferne korrupte Daten
    for (const key of keysToRemove) {
      kvRemove(key);
    }
    
    return { success: true, removedCount: keysToRemove.length };
    
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Validiert alle Business-Daten
 */
export const emergencyValidateData = () => {
  try {
    const businessKeys = listKvKeys().filter(
      (key) => key && key.includes('business_') && key.includes('_data')
    );
    
    const validationResults = [];
    
    for (const key of businessKeys) {
      try {
        const rawData = kvGetRaw(key);
        if (!rawData) {
          validationResults.push({ key, status: 'missing', error: 'No data found' });
          continue;
        }
        
        // Prüfe auf korrupte Daten
        if (rawData === '"[object Object]"' || rawData === '[object Object]' || rawData === 'null') {
          validationResults.push({ key, status: 'corrupted', error: `Corrupted data: ${rawData}` });
          continue;
        }
        
        const parsed = JSON.parse(rawData);
        
        // Validiere Struktur
        const requiredFields = ['products', 'suppliers', 'purchases', 'sales', 'lots'];
        const missingFields = requiredFields.filter(field => !(field in parsed));
        
        if (missingFields.length > 0) {
          validationResults.push({ 
            key, 
            status: 'incomplete', 
            error: `Missing fields: ${missingFields.join(', ')}` 
          });
        } else {
          validationResults.push({ key, status: 'valid' });
        }
        
      } catch (error) {
        validationResults.push({ key, status: 'corrupted', error: error.message });
      }
    }
    
    const validCount = validationResults.filter(r => r.status === 'valid').length;
    const corruptedCount = validationResults.filter(r => r.status === 'corrupted').length;
    const incompleteCount = validationResults.filter(r => r.status === 'incomplete').length;
    
    // Validation completed
    
    return {
      success: true,
      results: validationResults,
      summary: { validCount, corruptedCount, incompleteCount }
    };
    
  } catch (error) {
    return { success: false, error: error.message };
  }
};

/**
 * Vollständige Notfall-Wiederherstellung
 */
export const emergencyFullRecovery = () => {
  try {
    // 1. Validiere alle Daten
    const validation = emergencyValidateData();
    if (!validation.success) {
      return false;
    }
    
    // 2. Wenn korrupte Daten gefunden wurden, repariere sie
    if (validation.summary.corruptedCount > 0) {
      const repair = emergencyFixCorruptedData();
      
      if (!repair.success) {
        return false;
      }
    }
    
    // 3. Bereinige korrupte Daten
    const cleanup = emergencyCleanupCorruptedData();
    
    return true;
    
  } catch (error) {
    return false;
  }
};

// Exportiere für sofortige Verwendung
export default {
  emergencyFixCorruptedData,
  emergencyCleanupCorruptedData,
  emergencyValidateData,
  emergencyFullRecovery
};
